import { useEffect, useState } from 'react'
import moment from 'moment'
import { Skeleton } from '@mui/material'
import { Comment } from '@mui/icons-material'
import { useDispatch, useSelector } from 'react-redux'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Avatar, List, ListItem, ListItemAvatar, ListItemText, MenuItem, Select, Typography, Link } from '@mui/material'

import makeStyles from '@mui/styles/makeStyles'

import { useSnackbar } from 'notistack'
import { commentService } from '../../lib/services'
import { useFeature } from '../../lib/featureFlags'
import { logEvent } from '../../lib/amplitudeClient'
import { navigationCreators, widgetCreators } from '../../actions/creators'
import { authSelectors, moduleSelectors, widgetSelectors } from '../../selectors'

const useStyles = makeStyles(({ palette, spacing }) => ({
    filters: {
        '& > div': {
            margin: spacing(0, 1),
            '& .MuiOutlinedInput-input': {
                padding: spacing(1),
                paddingRight: 32,
                border: `1px solid ${palette.border?.main}`,
            },
            '& .MuiSelect-icon': {
                color: palette.text.primary,
            },
        },
    },
    commentList: { flex: 1, overflow: 'scroll' },
    endMessage: { width: '100%', display: 'inline-flex', justifyContent: 'center' },
    comment: { padding: spacing(0, 1) },
    liAvatar: { alignSelf: 'flex-start', minWidth: 38, marginTop: spacing(1) },
    avatar: {
        width: 30,
        height: 30,
        backgroundColor: palette.primary.main,
        '&:hover': { opacity: 1, cursor: 'default' },
        '& svg': { color: palette.primary.contrastText },
    },
    bodyText: {
        color: palette.text.primary,
        lineHeight: 1,
        overflow: 'hidden',
        whiteSpace: 'inherit',
        display: '-webkit-box',
        marginTop: spacing(0.5),
        textOverflow: 'ellipsis',
        '-webkitLineClamp': 2,
        '-webkitBoxOrient': 'vertical',
    },
    timestamp: {
        display: 'block',
        color: palette.text.primary,
        marginTop: '5px',
    },
}))

export default function ActivityFeed({ className }) {
    const classes = useStyles({})

    const [comments, setComments] = useState([])
    const [loading, setLoading] = useState(true)
    const [timeFilter, setTimeFilter] = useState('all')
    const [userFilter, setUserFilter] = useState('everyone')
    const [page, setPage] = useState(1)
    const [totalRecords, setTotalRecords] = useState(0)
    const [error, setError] = useState('')

    const [showComments] = useFeature('comments')

    const { enqueueSnackbar } = useSnackbar()
    const errorSnackbar = message => enqueueSnackbar(message, { variant: 'info' })

    const userId = useSelector(authSelectors.getUserId)
    const moduleId = useSelector(moduleSelectors.getCurrentModule).id

    const getComments = pgOverwrite => {
        const searchProps = { page: pgOverwrite || page }
        if (timeFilter !== 'all') {
            const startMoment = moment().subtract(timeFilter, 'days')
            searchProps.startDate = startMoment.format()
        }
        if (userFilter === 'me') searchProps.user = userId
        return commentService.getComments(moduleId, searchProps)
    }

    useEffect(() => {
        if (!showComments) return

        setError('')
        setLoading(true)
        setPage(1)
        getComments(1)
            .then(({ comments, totalRecords }) => {
                setComments(comments)
                setTotalRecords(totalRecords)
            })
            .catch(({ message }) => {
                console.error(message)
                //setError('No Comments')
            })
            .finally(() => setLoading(false))
    }, [timeFilter, userFilter])

    useEffect(() => {
        if (!showComments) return

        if (page !== 1) {
            getComments()
                .then(({ comments: newComments }) => setComments([...comments, ...newComments]))
                .catch(({ message }) => {
                    console.error(message)
                    //errorSnackbar('No Comments')
                })
        }
    }, [page])

    if (!showComments) return null

    return (
        <div className={className}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
                Activity Feed
            </Typography>
            <div className={classes.filters}>
                <Select variant="outlined" value={timeFilter} onChange={e => setTimeFilter(e.target.value)}>
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value={1}>Last 24 Hours</MenuItem>
                    <MenuItem value={7}>Last 7 Days</MenuItem>
                    <MenuItem value={30}>Last 30 Days</MenuItem>
                </Select>
                <Select variant="outlined" value={userFilter} onChange={e => setUserFilter(e.target.value)}>
                    <MenuItem value="everyone">Everyone</MenuItem>
                    <MenuItem value="me">Me</MenuItem>
                </Select>
            </div>
            {error ? (
                <Typography variant="h6" style={{ color: 'red' }}>
                    {error}
                </Typography>
            ) : (
                <List id="commentList" className={classes.commentList}>
                    {!loading && comments.length < 1 ? (
                        <Typography variant="h6" className={classes.endMessage}>
                            No activity found.
                        </Typography>
                    ) : loading ? (
                        <>
                            <Skeleton />
                            <Skeleton width={200} />
                            <Skeleton />
                        </>
                    ) : (
                        <InfiniteScroll
                            scrollableTarget="commentList"
                            dataLength={comments.length}
                            next={() => setPage(page + 1)}
                            hasMore={comments.length < totalRecords}
                            loader={
                                <>
                                    <Skeleton />
                                    <Skeleton width={200} />
                                    <Skeleton />
                                </>
                            }
                            endMessage={
                                <Typography variant="h6" className={classes.endMessage}>
                                    All caught up!
                                </Typography>
                            }
                        >
                            {comments.map(comment => {
                                return <CommentDisplay comment={comment} />
                            })}
                        </InfiniteScroll>
                    )}
                </List>
            )}
        </div>
    )
}

const CommentDisplay = ({ comment: { author, body, createdAt, updatedAt, containerId } }) => {
    const classes = useStyles({})

    const dispatch = useDispatch()

    const perspective = useSelector(state => widgetSelectors.getPerspectiveFromWidgetId(state, containerId))
    const widget = useSelector(state => widgetSelectors.getWidgetConfig(state, containerId))

    const getAuthorDisplay = author => {
        const { userId, firstName, lastName } = author
        if (firstName) {
            if (lastName) {
                return `${firstName} ${lastName}`
            } else {
                return firstName
            }
        } else {
            return userId
        }
    }

    const handleClick = () => {
        logEvent('ACTIVITY_LINK_CLICKED')
        dispatch(widgetCreators.setCommentingWidget(widget.Id))
        dispatch(navigationCreators.goToPerspective(perspective.Id))
    }

    if (!perspective || !widget) return null

    const createMoment = moment(createdAt)
    const updatedMoment = moment(updatedAt)
    const updated = updatedMoment.isAfter(createMoment)
    const dateFormat = 'MM/DD/YY h:mm a'

    return (
        <ListItem className={classes.comment}>
            <ListItemAvatar className={classes.liAvatar}>
                <Avatar className={classes.avatar}>
                    <Comment fontSize="small" />
                </Avatar>
            </ListItemAvatar>
            <ListItemText
                primary={
                    <>
                        <b>{getAuthorDisplay(author)}</b> left a comment on{' '}
                        <Link underline="always" onClick={handleClick}>
                            {widget.Title}
                        </Link>
                        .
                    </>
                }
                secondary={
                    <>
                        <span className={classes.bodyText}>"{body}"</span>
                        <span className={classes.timestamp}>
                            {updated ? `*Edited: ${updatedMoment.format(dateFormat)}` : createMoment.format(dateFormat)}
                        </span>
                    </>
                }
            ></ListItemText>
        </ListItem>
    )
}
